import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { updateMetaTags } from '../utils/seo'

const MacBookLogicboardPage: React.FC = () => {
  const navigate = useNavigate()
  const go = (path: string) => { navigate(path); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50) }

  useEffect(() => {
    updateMetaTags({
      title: 'MacBook Logicboard Reparatur – Mikrochip-Löten ab 119€ | PhoneMedic',
      description: 'MacBook Logicboard Reparatur ✓ Chip-Level Mikrochip-Löten ✓ ab 119€ statt 800€ Neukauf ✓ Wasserschaden & Kurzschluss ✓ 12 Monate Garantie ✓ Bundesweit | PhoneMedic',
      keywords: ['MacBook Logicboard Reparatur', 'MacBook Platinen Reparatur', 'Mikrochip Löten', 'MacBook Chip Reparatur', 'BGA Reparatur MacBook', 'MacBook Mainboard'],
      canonicalUrl: 'https://www.phone-medic.de/macbook-logicboard-reparatur',
    })

    const schema = [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'MacBook Logicboard Reparatur – Chip-Level Mikrochip-Löten',
        description: 'Professionelle MacBook Logicboard Reparatur auf Chip-Ebene. BGA-Löten, PMIC, T2, USB-C Controller – wir reparieren, was Apple nicht kann.',
        provider: { '@type': 'LocalBusiness', name: 'PhoneMedic', url: 'https://www.phone-medic.de', telephone: '+491605010907' },
        offers: { '@type': 'Offer', price: '119.00', priceCurrency: 'EUR', priceValidUntil: '2026-12-31' },
        areaServed: 'DE',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Was ist ein MacBook Logicboard?',
            acceptedAnswer: { '@type': 'Answer', text: 'Das Logicboard (auch Mainboard oder Platine genannt) ist die Hauptplatine des MacBooks. Auf ihr sitzen CPU, RAM, GPU, Speicherchips, der T2/T1-Sicherheitschip, USB-C Controller und alle weiteren ICs. Ein defektes Logicboard legt das gesamte MacBook lahm.' },
          },
          {
            '@type': 'Question',
            name: 'Was bedeutet Chip-Level Reparatur?',
            acceptedAnswer: { '@type': 'Answer', text: 'Bei einer Chip-Level Reparatur werden einzelne Chips oder Bauteile auf der Platine unter dem Mikroskop identifiziert und gezielt ausgetauscht – statt die gesamte Platine zu ersetzen. Das spart bis zu 90% gegenüber einem Logicboard-Tausch.' },
          },
          {
            '@type': 'Question',
            name: 'Was kostet eine Logicboard Reparatur?',
            acceptedAnswer: { '@type': 'Answer', text: 'Die Diagnose kostet 24,95 €. Die eigentliche Reparatur beginnt ab 119 €. Apple verlangt für einen Logicboard-Tausch 800–1.500 €. Wir reparieren den defekten Chip – deutlich günstiger und mit Datenerhalt.' },
          },
          {
            '@type': 'Question',
            name: 'Kann Apple MacBook Logicboards reparieren?',
            acceptedAnswer: { '@type': 'Answer', text: 'Nein. Apple bietet keinen Chip-Level Service an. Apple tauscht defekte Logicboards komplett aus – zum Preis von 800–1.500 € oder empfiehlt einen Neukauf. PhoneMedic repariert den defekten Chip direkt.' },
          },
          {
            '@type': 'Question',
            name: 'Welche Logicboard-Defekte können repariert werden?',
            acceptedAnswer: { '@type': 'Answer', text: 'PMIC (Power Management IC), T2/T1-Sicherheitschip, USB-C Controller, Backlight-IC, Kurzschlüsse, Oxidation nach Wasserschaden, defekte Kondensatoren und Widerstände, BIOS-Chip, TouchID-Controller.' },
          },
        ],
      },
    ]

    let script = document.querySelector('#macbook-logicboard-schema') as HTMLScriptElement
    if (!script) {
      script = document.createElement('script')
      script.id = 'macbook-logicboard-schema'
      script.type = 'application/ld+json'
      document.head.appendChild(script)
    }
    script.textContent = JSON.stringify(schema)
    return () => { document.querySelector('#macbook-logicboard-schema')?.remove() }
  }, [])

  const repairs = [
    { icon: '⚡', title: 'PMIC Reparatur', desc: 'Der Power Management IC steuert alle Spannungen auf der Platine. Ein Defekt bedeutet: kein Start, kein Laden, kein Lebenszeichen. Häufig durch Kurzschlüsse oder Überspannung verursacht.' },
    { icon: '🔒', title: 'T2 / T1 Chip', desc: 'Apples Sicherheitschip T2 verwaltet Verschlüsselung, TouchID und den sicheren Boot. Defekte können Boot-Loops, schwarze Bildschirme oder komplette Startausfälle verursachen.' },
    { icon: '🔌', title: 'USB-C Controller', desc: 'Der USB-C Controller verwaltet Laden und Datentransfer. Defekt: MacBook lädt nicht, wird nicht erkannt, oder nur eine Seite funktioniert.' },
    { icon: '💧', title: 'Wasserschaden-Reparatur', desc: 'Flüssigkeit hinterlässt Korrosion, die Leiterbahnen unterbricht und Chips beschädigt. Unter dem Mikroskop reinigen und beschädigte Bauteile ersetzen.' },
    { icon: '🖥️', title: 'Backlight IC / Display-Controller', desc: 'Kein Bild oder kein Hintergrundbild? Oft ein defekter Backlight-IC oder Display-Controller auf der Platine – kein Display-Tausch nötig.' },
    { icon: '🧩', title: 'SMD-Chips & Kondensatoren', desc: 'Kleine SMD-Bauteile wie Kondensatoren, Widerstände und Dioden können altern oder bei Überspannungen versagen. Präzise Reparatur unter dem Mikroskop.' },
  ]

  const faqs = [
    { q: 'Was ist ein MacBook Logicboard?', a: 'Das Logicboard ist die Hauptplatine des MacBooks. Auf ihr sitzen CPU, RAM, GPU, T2-Sicherheitschip, USB-C Controller und alle weiteren ICs. Ein Defekt legt das gesamte MacBook lahm.' },
    { q: 'Was bedeutet Chip-Level Reparatur?', a: 'Einzelne Chips auf der Platine werden unter dem Mikroskop identifiziert und gezielt ausgetauscht – statt die gesamte Platine zu ersetzen. Das spart bis zu 90% der Kosten.' },
    { q: 'Was kostet eine Logicboard Reparatur?', a: 'Diagnose 24,95€. Reparatur ab 119€. Apple verlangt 800–1.500€ für einen Logicboard-Tausch. Wir reparieren den defekten Chip direkt.' },
    { q: 'Kann Apple MacBook Logicboards reparieren?', a: 'Nein. Apple bietet keinen Chip-Level Service. Apple tauscht defekte Logicboards komplett aus oder empfiehlt Neukauf. Wir reparieren den defekten Chip direkt.' },
    { q: 'Welche Logicboard-Defekte können repariert werden?', a: 'PMIC, T2/T1-Chip, USB-C Controller, Backlight-IC, Kurzschlüsse, Wasserschaden-Korrosion, defekte Kondensatoren, BIOS-Chip, TouchID-Controller.' },
    { q: 'Werden Daten bei der Reparatur gelöscht?', a: 'Nein. Der Speicherchip wird bei der Logicboard-Reparatur nicht verändert. Daten bleiben erhalten. Wir informieren dich vorher falls der Speicher betroffen sein könnte.' },
  ]

  return (
    <div className="min-h-screen bg-white">
      <section
        className="relative text-white py-20"
        style={{ backgroundImage: 'url(/hero-bg.webp)', backgroundSize: 'cover', backgroundPosition: 'center top' }}
      >
        <div className="absolute inset-0 bg-gray-900/80" />
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-indigo-600/20 border border-indigo-500/30 text-indigo-300 text-sm font-medium px-3 py-1 rounded-full mb-6">
              <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full"></span>
              Chip-Level · BGA-Löten · Mikroskop-Reparatur
            </div>
            <h1 className="text-5xl font-bold mb-6 leading-tight">
              MacBook Logicboard Reparatur –<br />
              <span className="text-indigo-400">Mikrochip-Löten statt Neukauf</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl">
              Apple sagt „neues Logicboard für 1.200 €"? Wir reparieren den defekten Chip direkt – ab 119 €, mit 12 Monaten Garantie und bundesweitem Einsende-Service.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="mailto:service@phone-medic.de" className="bg-indigo-600 text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors">
                Kostenvoranschlag anfragen
              </a>
              <button onClick={() => go('/macbook-reparatur')} className="bg-white/10 text-white border border-white/20 px-7 py-3.5 rounded-lg font-semibold hover:bg-white/20 transition-colors">
                Alle MacBook Services
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gray-800 text-white py-5 border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[
              { number: 'ab 119 €', label: 'statt 800–1.500 € (Apple)' },
              { number: '24,95 €', label: 'Diagnose' },
              { number: '70–90 %', label: 'Erfolgsquote' },
              { number: '12 Monate', label: 'Garantie' },
            ].map((s, i) => (
              <div key={i}>
                <div className="text-2xl font-bold text-indigo-400">{s.number}</div>
                <div className="text-sm text-gray-400">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Was wir reparieren können</h2>
            <p className="text-gray-500 max-w-xl mx-auto">Chip-Level Reparaturen auf der MacBook Platine – was Apple und normale Werkstätten ablehnen</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {repairs.map((r, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-bold text-gray-900 mb-2">{r.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Warum Chip-Level statt Logicboard-Tausch?</h2>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Ein MacBook Logicboard kostet neu zwischen 800 und 1.500 € – ohne Einbau. Viele Kunden entscheiden sich dann für ein neues MacBook, obwohl das alte Gerät nur einen einzigen defekten Chip hat.
              </p>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Mit dem richtigen Equipment – Stereomikroskop, Heißluft-Lötstation, Infrarot-Thermometer und BGA-Löt-Technik – lässt sich in den meisten Fällen genau dieser Chip identifizieren und austauschen.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Das Ergebnis: Dein MacBook läuft wieder wie neu. Deine Daten bleiben erhalten. Du sparst 700–1.300 € gegenüber dem Logicboard-Tausch bei Apple.
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-6">
              <h3 className="font-bold text-gray-900 mb-4">Kostenvergleich</h3>
              <div className="space-y-4">
                {[
                  { label: 'Neukauf MacBook', cost: '1.200–2.500 €', highlight: false, bad: true },
                  { label: 'Apple Logicboard-Tausch', cost: '800–1.500 €', highlight: false, bad: true },
                  { label: 'Normale Werkstatt (aufgegeben)', cost: '0 € Reparatur, Neukauf empfohlen', highlight: false, bad: true },
                  { label: 'PhoneMedic Chip-Level Reparatur', cost: 'ab 119 €', highlight: true, bad: false },
                ].map((item, i) => (
                  <div key={i} className={`flex justify-between items-center p-4 rounded-lg ${item.highlight ? 'bg-indigo-600 text-white' : 'bg-white border border-gray-200'}`}>
                    <span className={`text-sm font-medium ${item.highlight ? 'text-white' : item.bad ? 'text-gray-500' : 'text-gray-700'}`}>{item.label}</span>
                    <span className={`text-sm font-bold ${item.highlight ? 'text-white' : item.bad ? 'text-red-500' : 'text-gray-900'}`}>{item.cost}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-10">Häufige Fragen</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Weitere MacBook Services</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'MacBook startet nicht', path: '/macbook-startet-nicht' },
              { label: 'MacBook Wasserschaden', path: '/macbook-wasserschaden' },
              { label: 'MacBook Akku tauschen', path: '/macbook-akku-tauschen' },
              { label: 'MacBook Display Reparatur', path: '/macbook-display-reparatur' },
            ].map((item) => (
              <button key={item.path} onClick={() => go(item.path)} className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm font-medium text-gray-700 hover:border-indigo-400 hover:text-indigo-700 hover:bg-indigo-50 transition-colors text-center">
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-3">Logicboard defekt? Wir reparieren den Chip.</h2>
          <p className="text-gray-400 mb-8">Diagnose 24,95 € · Reparatur ab 119 € · Kostenvoranschlag vor Reparaturbeginn</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="mailto:service@phone-medic.de" className="bg-indigo-600 text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors">
              Per E-Mail anfragen
            </a>
            <a href="https://wa.me/491605010907" target="_blank" rel="noopener noreferrer" className="bg-white text-gray-900 px-7 py-3.5 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}

export default MacBookLogicboardPage
